import { type ReactNode, useState } from 'react';

import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { type LucideIcon, Plus } from 'lucide-react';

export type CardAddMenuOption = {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
};

type CardAddMenuProps = {
  options: CardAddMenuOption[];
  tooltip?: string;
  children?: ReactNode;
  disabled?: boolean;
};

export const CardAddMenu = ({
  options,
  tooltip,
  children,
  disabled,
}: CardAddMenuProps) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  const enabledOptions = options.filter(option => !option.disabled);
  const onlyOne = enabledOptions.length === 1 ? enabledOptions[0] : null;

  const button = (
    <IconButton
      size="small"
      onClick={evt => {
        if (onlyOne) {
          onlyOne.onClick();
          return;
        }
        setAnchor(evt.currentTarget);
      }}
      disabled={disabled || enabledOptions.length === 0}
      sx={{
        color: 'primary.main',
        backgroundColor: 'action.hover',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      {children ?? <Plus size={16} />}
    </IconButton>
  );

  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map(option => (
          <MenuItem
            key={option.id}
            onClick={() => {
              setAnchor(null);
              option.onClick();
            }}
            disabled={option.disabled}
          >
            {option.icon && (
              <ListItemIcon>
                <option.icon size={14} />
              </ListItemIcon>
            )}
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
